/**
 * Minimal fetch wrapper for the Go backend.
 *
 * - Base URL from VITE_API_URL; empty in production means same-origin, and the
 *   dev server proxies /api to localhost:8080 (vite.config.ts).
 * - Sends credentials so the docai_session cookie flows even if the API origin
 *   ever differs from the app origin in dev.
 * - Backend errors arrive as {"error": {code, message, details?}} and are
 *   rethrown as ApiError so screens can branch on the stable `code`.
 */

const BASE_URL = import.meta.env.VITE_API_URL || ''

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: Record<string, unknown> }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>
  /** Parsed from the Retry-After header on 429 responses; 0 when absent. */
  readonly retryAfterSeconds: number

  constructor(status: number, code: string, message: string, details: Record<string, unknown>, retryAfterSeconds: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.retryAfterSeconds = retryAfterSeconds
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })
  return parseResponse<T>(response)
}

/**
 * Multipart upload variant of apiFetch: no Content-Type header (the browser
 * sets it with the form boundary), same cookie and error-envelope handling.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    body: form,
    credentials: 'include',
  })
  return parseResponse<T>(response)
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T

  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    // Non-JSON body (proxy error page etc.) — fall through to status handling.
  }

  if (!response.ok) {
    const envelope = (body ?? {}) as ErrorEnvelope
    const retryAfter = Number(response.headers.get('Retry-After')) || 0
    throw new ApiError(
      response.status,
      envelope.error?.code ?? 'unknown',
      envelope.error?.message ?? `Request failed with status ${response.status}`,
      envelope.error?.details ?? {},
      retryAfter,
    )
  }

  return body as T
}
